import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@tractian/ui'

import { isChunkLoadError } from '@/app/chunk-load-error'
import { reloadAfterChunkError } from '@/app/refresh-cache'
import { paths } from '@/routes/routes'

function GenericFallback({ error }: { error: unknown }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (isRouteErrorResponse(error)) {
    return (
      <main className="flex h-full flex-col items-center justify-center gap-3 bg-gray-50 p-6 text-center">
        <h1 className="text-xl font-semibold text-gray-900">{t('errorBoundary.genericTitle')}</h1>
        <p className="text-sm text-gray-500">
          {error.status} {error.statusText}
        </p>
        <Button onClick={() => navigate(paths.root())}>{t('errorBoundary.backHome')}</Button>
      </main>
    )
  }

  return (
    <main className="flex h-full flex-col items-center justify-center gap-3 bg-gray-50 p-6 text-center">
      <h1 className="text-xl font-semibold text-gray-900">{t('errorBoundary.genericTitle')}</h1>
      <p className="max-w-md text-sm text-gray-500">{t('errorBoundary.genericDescription')}</p>
      <div className="flex gap-2">
        <Button onClick={() => window.location.reload()}>{t('errorBoundary.reload')}</Button>
        <Button variant="outline" onClick={() => navigate(paths.root())}>
          {t('errorBoundary.backHome')}
        </Button>
      </div>
    </main>
  )
}

export function RouteErrorBoundary() {
  const error = useRouteError()

  if (isChunkLoadError(error)) {
    return <ChunkLoadErrorRecovery />
  }

  return <GenericFallback error={error} />
}

/**
 * Chunk-load failures are a stale tab across a deploy (the old hashed chunk is
 * gone, or a rolling deploy routed the request to a pod with the other build).
 * Never show an error: keep reloading with a capped backoff until the new
 * bundle loads, like a manual refresh would.
 */
function ChunkLoadErrorRecovery() {
  const { t } = useTranslation()

  useEffect(() => {
    reloadAfterChunkError()
  }, [])

  return (
    <main className="flex h-full flex-col items-center justify-center gap-3 bg-gray-50 p-6 text-center">
      <p className="text-sm text-gray-500">{t('errorBoundary.updating')}</p>
    </main>
  )
}
